import { HttpStatusCode } from 'axios';
import React, { useEffect } from 'react';
import {
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toastNotify } from '../Helper';
import {
  increment,
  reset,
} from '../Storage/Redux/slice/errorBoundaryCounterSlice';
import { setPageTitle } from '../Storage/Redux/slice/themeConfigSlice';
import { IRootState } from '../Storage/Redux/store';

// Helper function to parse the stack trace
function parseStackTrace(error: Error) {
  const stackLines = error?.stack?.split('\n');
  if (stackLines && stackLines.length > 1) {
    // Adjust the regular expression for your specific needs and environment
    const match = stackLines[1].match(/\(?(.+):(\d+):(\d+)\)?$/);
    if (match) {
      return {
        source: match[1],
        lineno: parseInt(match[2], 10),
        colno: parseInt(match[3], 10),
      };
    }
  }
  return { source: null, lineno: null, colno: null };
}

// Fallback UI for when an error is caught
const FallbackUI: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const dispatch = useDispatch();
  const isDark = useSelector(
    (state: IRootState) =>
      state.themeConfig.theme === 'dark' || state.themeConfig.isDarkMode
  );
  const resetCount = useSelector(
    (state: IRootState) => state.errorBoundaryCounterReducer.count ?? 0
  );
  const { source, lineno, colno } = parseStackTrace(error);

  useEffect(() => {
    dispatch(setPageTitle('Network Error'));
  });

  // Log errors only in production
  if (process.env.NODE_ENV === 'production') {
    logErrorToGoogleSheet({
      message: error.message,
      source: source,
      lineno: lineno,
      colno: colno,
      stack: error.stack,
    });
  }

  return (
    <div>
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div className="ppx-6 py-16 text-center font-semibold before:container before:absolute before:left-1/2 before:-translate-x-1/2 before:rounded-full before:bg-[linear-gradient(180deg,#4361EE_0%,rgba(67,97,238,0)_50.73%)] before:aspect-square before:opacity-10 md:py-20">
          <div className="relative">
            <img
              src={
                isDark
                  ? '/assets/images/error/500-dark.svg'
                  : '/assets/images/error/500-light.svg'
              }
              alt="500"
              className="mx-auto -mt-10 w-full max-w-xs object-cover md:-mt-14 md:max-w-xl"
            />
            <p className="mt-5 text-base dark:text-white">
              Internal server error!
            </p>
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-5 p-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg text-left text-sm">
                <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                  Error Details
                </h2>
                <div className="mb-2">
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    <strong>Error:</strong> {error.message}
                  </p>
                </div>
                <div className="mb-2">
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    <strong>Source:</strong> {source || 'Unknown'}
                  </p>
                </div>
                <div className="mb-2">
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    <strong>Line:</strong> {lineno || 'Unknown'}
                  </p>
                </div>
                <div className="mb-2">
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    <strong>Column:</strong> {colno || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    <strong>Stack Trace:</strong>
                  </p>
                  <pre className="whitespace-pre-wrap text-red-600 dark:text-red-400 bg-gray-100 dark:bg-gray-900 p-3 rounded-md mt-2">
                    {error.stack}
                  </pre>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                dispatch(increment());
                resetErrorBoundary();
              }}
              className="btn btn-gradient mx-auto !mt-7 w-max border-0 uppercase shadow-none"
            >
              {resetCount > 2 ? 'Home' : 'Try again'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Function to send error logs to Google Sheets or another service
const logErrorToGoogleSheet = async (errorData: any) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_GoogleExcelLink}`, {
      method: 'POST',
      body: JSON.stringify(errorData),
    });

    const result = await response.json();

    if (result.status === 'error') {
      toastNotify(`Error logging to Google Sheets: ${result.message}`, 'error');
    } else {
      toastNotify(
        `${HttpStatusCode.InternalServerError}: Network Error!`,
        'error'
      );
    }
  } catch (networkError) {
    toastNotify(`Network error: ${networkError}`, 'error');
  }
};

const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetCount = useSelector(
    (state: IRootState) => state.errorBoundaryCounterReducer.count ?? 0
  );

  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackUI}
      onReset={() => {
        if (resetCount > 2) {
          dispatch(reset());
          navigate('/');
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
